/**
 * The moment between joining a course and starting it.
 *
 * A screen that only says "well done" is a screen in the way. This one exists because the
 * step it replaces is worse: an enrolment that silently flips a button label, leaving
 * someone on a marketing page wondering whether it worked and where the course went. So it
 * does three things and then gets out of the way — CONFIRMS (from the server, not from the
 * fact that a request was sent), SHOWS what was joined, and LEADS straight into the first
 * unit.
 *
 * It confirms by re-reading `/api/course_home/course_metadata/{id}`, which is the same
 * query key the course page uses, so the check is usually already in cache. If the server
 * does not agree that this person is enrolled, the screen says so instead of congratulating
 * them — a false confirmation is worse than no screen at all, because it sends someone away
 * believing they have a place they do not have.
 *
 * Layout is the prototype's `#screen-enrollment-success`.
 */

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon, Spinner } from '../../shared/ui';
import {
  courseHref,
  courseImage,
  formatDate,
  getCourse,
  visibleToTenant,
  getCourseMetadata,
  startLabel,
  type CourseDetail,
} from '../../shared/lib/catalogue';
import './unit.css';

export default function EnrolmentSuccess() {
  const params = useParams();
  const [search] = useSearchParams();
  // The route this is wired to is not this file's to choose, so the course id is taken
  // from the path if it is there and from the query string if it is not. One of the two
  // always is, and guessing wrong would strand someone on a page with no course.
  const courseId = params.courseId ?? search.get('course') ?? '';

  const course = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => getCourse(courseId),
    enabled: Boolean(courseId),
    retry: false,
  });

  const metadata = useQuery({
    queryKey: ['course-metadata', courseId],
    queryFn: () => getCourseMetadata(courseId),
    enabled: Boolean(courseId),
    retry: false,
  });

  if (!courseId) return <CouldNotConfirm courseId="" reason="missing" />;

  if (course.isPending || metadata.isPending) {
    return (
      <main className="success-wrap">
        <Spinner label="Confirming your place…" />
      </main>
    );
  }

  if (course.isError || metadata.isError) {
    return <CouldNotConfirm courseId={courseId} reason="unreachable" />;
  }

  // Two ways in — this academy's org, or a licence on this one course. The org-only test
  // would tell a learner who just enrolled in a licensed course that it is not ours.
  if (!visibleToTenant(courseId, metadata.data.org)) {
    return <CouldNotConfirm courseId={courseId} reason="other-tenant" />;
  }

  // Staff reach the courseware through their role, so this screen is honest for them too —
  // but it must not tell them they are enrolled when they are not.
  if (!metadata.data.is_enrolled) {
    return <CouldNotConfirm courseId={courseId} reason="not-enrolled" />;
  }

  return <Confirmed course={course.data} />;
}

/* --------------------------------------------------------------- confirmed ----- */

function Confirmed({ course }: { course: CourseDetail }) {
  const image = courseImage(course.media);
  const [imageFailed, setImageFailed] = useState(false);
  const start = startLabel(course);

  return (
    <main className="success-wrap">
      <div className="success-check" aria-hidden="true">
        <Icon name="check" />
      </div>

      <h1>You&rsquo;re enrolled 🎉</h1>
      <p>
        You have a place on <strong>{course.name}</strong>. It is on your dashboard from now
        on, and it will open wherever you left off.
      </p>

      <div className="success-card">
        {image && !imageFailed ? (
          <img
            src={image}
            alt=""
            className="success-card__thumb success-card__thumb--image"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="success-card__thumb" aria-hidden="true">
            <Icon name="school" />
          </div>
        )}

        <div>
          <p className="success-card__title">{course.name}</p>
          <span className="success-card__meta">
            {[
              start ? `Starts ${start}` : null,
              course.end ? `Ends ${formatDate(course.end)}` : null,
              course.pacing === 'self' ? 'Learn at your own pace' : 'Instructor paced',
              course.effort,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      </div>

      <div className="success-actions">
        {/* Straight into the courseware, not to a table of contents. `/learn/:courseId`
            resolves to the first unit on a fresh enrolment and to the resume point later,
            so the same link is right on day one and on day thirty. */}
        <Link
          to={`/learn/${encodeURIComponent(course.id)}`}
          className="ui-btn"
          data-variant="primary"
        >
          <Icon name="play_arrow" />
          Start learning
        </Link>
        <Link to="/dashboard" className="ui-btn" data-variant="secondary">
          <Icon name="school" />
          Go to my dashboard
        </Link>
      </div>

      <div className="success-checklist">
        <h3>What happens next</h3>
        <ul>
          <li>
            <Icon name="bookmark" />
            The course remembers where you stopped — reopen it any time and carry on from
            the same unit.
          </li>
          <li>
            <Icon name="update" />
            Anything the course team publishes later appears in your outline automatically.
          </li>
          <li>
            <Icon name="logout" />
            You can leave the course at any time from your dashboard.
          </li>
        </ul>
      </div>
    </main>
  );
}

/* ----------------------------------------------------------- not confirmed ----- */

/**
 * What to say when the server does not agree that this person is enrolled.
 *
 * Never "something went wrong". Each of these is a different situation with a different
 * next step, and the only one that is genuinely unknown says so.
 */
function CouldNotConfirm({
  courseId,
  reason,
}: {
  courseId: string;
  reason: 'missing' | 'unreachable' | 'other-tenant' | 'not-enrolled';
}) {
  return (
    <main className="success-wrap">
      <div className="success-check success-check--problem" aria-hidden="true">
        <Icon name="priority_high" />
      </div>

      <h1>
        {reason === 'not-enrolled'
          ? 'We could not confirm your place'
          : reason === 'other-tenant'
            ? 'That course is not part of this academy'
            : 'We could not confirm your enrolment'}
      </h1>

      <p>
        {reason === 'not-enrolled'
          ? 'The academy does not show you as enrolled in this course. Nothing has been charged or changed — open the course page and enrol from there.'
          : reason === 'other-tenant'
            ? 'If someone sent you this link, they may have meant a different site.'
            : reason === 'missing'
              ? 'This page arrived without a course, so there is nothing to confirm. Your dashboard lists every course you have joined.'
              : 'The academy did not answer. Your enrolment may well have gone through — your dashboard is the place that knows.'}
      </p>

      <div className="success-actions">
        {courseId && reason !== 'other-tenant' && (
          <Link to={courseHref(courseId)} className="ui-btn" data-variant="primary">
            Go to the course page
          </Link>
        )}
        <Link to="/dashboard" className="ui-btn" data-variant="secondary">
          Go to my dashboard
        </Link>
      </div>
    </main>
  );
}
